// Hono RPC client placeholder. Filled in Phase 1 when AppType is wired up.
// Until then, use plain `fetch(env.VITE_API_URL + path)` in hooks.

import { env } from './env';

export const apiBaseUrl = env.VITE_API_URL;

/**
 * Einen von der API gelieferten PFAD zu einer Adresse machen, die der Browser
 * erreicht.
 *
 * Objektfotos kommen seit Batch C.8 als Pfad („/properties/…/image/…"), nicht
 * als fertige Adresse — die API weiss nicht, unter welchem Ursprung sie
 * erreicht wird. In der Produktion ist `VITE_API_URL` absolut
 * (`https://api.4m-immo.com`), und `public/_routes.json` leitet nur `/api/*`
 * und `/listing/*` durch Functions: ein roher Pfad im `src` eines `<img>`
 * landet also beim SPA-Fallback, bekommt `index.html` mit Status 200
 * ausgeliefert und zeigt ein kaputtes Bild.
 *
 * Deshalb steht die Auflösung hier und nicht in einer Komponente — dieselbe
 * Auflösung, die `<Avatar>` und `mediaFileUrl` schon machen; der Session-Cookie
 * gilt für die ganze Domain.
 */
export function apiAssetUrl(url: string): string {
  return url.startsWith('/') ? `${apiBaseUrl}${url}` : url;
}

// Batch ZT (Task 1) — guard against firing the idle-timeout broadcast
// multiple times when several in-flight requests all hit 401 in the same
// instant. Subscribers (useIdleTimer's listener at module init) only need
// to react once per cycle.
let idleBroadcastedAt = 0;
function maybeBroadcastIdle(): void {
  const now = Date.now();
  if (now - idleBroadcastedAt < 2_000) return;
  idleBroadcastedAt = now;
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('4m:idle-timeout'));
  }
}

// A 401 on these paths is an expected, normal outcome — NOT a dead session, so
// it must never trigger the logout+redirect:
//   /auth/agent, /auth/api/*  login attempts (a wrong password legitimately 401s;
//                             redirecting would bounce the user and wipe the
//                             on-screen error message)
//   /auth/me                  the `_app` route guard's own "am I logged in?" probe,
//                             which already redirects to /login by itself
// Note `/auth/me/...` sub-paths (password, avatar, appearance) are deliberately
// NOT excluded — a 401 there really does mean the session died.
function isExpected401(path: string): boolean {
  return (
    path.startsWith('/auth/agent') ||
    path.startsWith('/auth/api/') ||
    path === '/auth/me' ||
    path.startsWith('/auth/me?')
  );
}

export async function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const isFormData = init.body instanceof FormData;
  const headers = new Headers(init.headers ?? {});
  if (!isFormData && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  const res = await fetch(apiBaseUrl + path, {
    credentials: 'include',
    ...init,
    headers,
  });
  // Session-expired handling. The backend produces 401 in TWO shapes:
  //   a) the exact moment the idle gate fires → `X-Session-Expired: idle_timeout`
  //   b) every later request on an already-dead session → plain 401
  //      `unauthenticated` from requireAuth, with NO header.
  // Originally only (a) broadcast, so a user whose session had already been torn
  // down never got redirected — they sat on a dead page where every action failed
  // with a raw "unauthenticated" string (reported 2026-07-22 via the Zoki widget:
  // three messages, three cryptic errors, no hint to log in again).
  // Both shapes now broadcast so the global IdleTimerProvider logs out and
  // redirects to /login — still only ONCE per cycle even when many parallel
  // requests fail together.
  if (res.status === 401 && !isExpected401(path)) {
    maybeBroadcastIdle();
  }
  return res;
}
